import React, { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { XMarkIcon, CheckIcon } from "@heroicons/react/24/outline";
import { BoltIcon } from "@heroicons/react/24/solid";
import { toast } from "sonner";
import { apiClient } from "../../lib/axios";
import { ApiPlan, PlanCard, buildPlanCards } from "../../lib/plans";
import { submitPayuForm } from "../../lib/payu";
import { openRazorpayCheckout } from "../../lib/razorpay";

interface PlansModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchaseSuccess: (credits: number) => void;
}

interface CouponValidation {
  valid: boolean;
  discount_type?: string;
  discount_value?: number;
  final_price?: number;
  is_free?: boolean;
  message?: string;
}

export default function PlansModal({ isOpen, onClose, onPurchaseSuccess }: PlansModalProps) {
  const [cards, setCards] = useState<PlanCard[]>([]);
  const [customPlans, setCustomPlans] = useState<ApiPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [purchasingId, setPurchasingId] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponValidation, setCouponValidation] = useState<CouponValidation | null>(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [hoveredPlan, setHoveredPlan] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setCouponCode('');
      setCouponValidation(null);
      setSelectedIdx(null);
      Promise.all([
        apiClient.get('/plans'),
        apiClient.get('/user/custom-plans').catch(() => ({ data: [] })),
      ])
        .then(([plansRes, customRes]) => {
          setCards(buildPlanCards(plansRes.data as ApiPlan[]));
          setCustomPlans(customRes.data as ApiPlan[]);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [isOpen]);

  const handleCustomPurchase = async (plan: ApiPlan) => {
    setPurchasingId(plan.id);
    try {
      const orderRes = await apiClient.post('/payment/create-order', { plan_id: plan.id });
      const data = orderRes.data;
      if (data.gateway === 'razorpay') {
        onClose();
        try {
          const payment = await openRazorpayCheckout(data);
          const verifyRes = await apiClient.post('/payment/razorpay/verify', payment);
          toast.success('Payment successful! Your credits have been added.');
          onPurchaseSuccess(verifyRes.data.credits);
        } catch (rzpErr: any) {
          if (rzpErr?.message !== 'Payment cancelled') {
            toast.error(rzpErr?.response?.data?.error || rzpErr?.message || 'Payment failed.');
          }
        }
        setPurchasingId(null);
        return;
      }
      toast.message('Redirecting to secure checkout…');
      submitPayuForm(data.action_url, data.params);
    } catch (e: any) {
      if ((e as Error)?.message === 'Payment cancelled') { setPurchasingId(null); return; }
      toast.error((e as any).response?.data?.error || (e as Error).message || 'Error initiating payment');
      setPurchasingId(null);
    }
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    if (selectedIdx === null || !cards[selectedIdx]) {
      toast.error('Select a plan first');
      return;
    }
    setValidatingCoupon(true);
    try {
      const res = await apiClient.post('/coupons/validate', {
        code: couponCode.toUpperCase(),
        plan_id: cards[selectedIdx].id,
      });
      if (res.data.valid) {
        setCouponValidation(res.data);
        toast.success(`${res.data.discount_value}${res.data.discount_type === 'percentage' ? '%' : '$'} off applied`);
      } else {
        setCouponValidation(null);
        toast.error(res.data.message || 'Invalid coupon code');
      }
    } catch (e: any) {
      setCouponValidation(null);
      toast.error(e.response?.data?.message || 'Error validating coupon');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleClearCoupon = () => {
    setCouponCode('');
    setCouponValidation(null);
  };

  const handlePurchase = async (idx: number) => {
    const card = cards[idx];
    if (!card) { toast.error('Plan unavailable'); return; }
    setSelectedIdx(idx);
    setPurchasingId(card.id);
    try {
      const isFree = couponValidation?.is_free ?? false;

      if (isFree) {
        const verifyRes = await apiClient.post('/user/purchase', {
          plan_id: card.id,
          coupon_code: couponCode.toUpperCase(),
          is_free: true,
        });
        if (verifyRes.status === 200) {
          toast.success('Plan activated successfully!');
          onPurchaseSuccess(verifyRes.data.credits);
          onClose();
        }
        return;
      }

      const orderRes = await apiClient.post('/payment/create-order', {
        plan_id: card.id,
        ...(couponCode && { coupon_code: couponCode.toUpperCase() }),
      });

      const data = orderRes.data;

      if (data.gateway === 'razorpay') {
        // Close modal first — Radix UI's focus trap blocks interaction with the
        // Razorpay iframe if the dialog stays open.
        onClose();
        try {
          const payment = await openRazorpayCheckout(data);
          const verifyRes = await apiClient.post('/payment/razorpay/verify', payment);
          toast.success('Payment successful! Your credits have been added.');
          onPurchaseSuccess(verifyRes.data.credits);
        } catch (rzpErr: any) {
          if (rzpErr?.message !== 'Payment cancelled') {
            toast.error(rzpErr?.response?.data?.error || rzpErr?.message || 'Payment failed.');
          }
        }
        setPurchasingId(null);
        return;
      }

      // PayU hosted checkout — redirect the browser to the gateway.
      toast.message('Redirecting to secure checkout…');
      submitPayuForm(data.action_url, data.params);
      // page unloads; result handled on /dashboard/billing?payment=…
    } catch (e: any) {
      if ((e as Error)?.message === 'Payment cancelled') { setPurchasingId(null); return; }
      toast.error((e as any).response?.data?.error || (e as Error).message || 'Error initiating payment');
      setPurchasingId(null);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />

        <Dialog.Content className="fixed left-[50%] top-[50%] z-[101] w-full max-w-5xl translate-x-[-50%] translate-y-[-50%] p-4 focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] duration-300">

          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

            {/* Header */}
            <div className="px-8 py-5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <Dialog.Title className="text-xl font-extrabold text-slate-900 tracking-tight">Get Credits</Dialog.Title>
                <Dialog.Description className="text-sm text-slate-500 mt-0.5">One credit = one company. All contacts included.</Dialog.Description>
              </div>
              <Dialog.Close className="w-9 h-9 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-900 transition-colors">
                <XMarkIcon className="w-5 h-5" />
              </Dialog.Close>
            </div>

            {/* Content */}
            <div className="p-8 overflow-y-auto bg-white">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wide animate-pulse">Loading…</p>
                </div>
              ) : cards.length === 0 ? (
                <div className="py-20 text-center text-sm font-bold text-slate-400">No plans available right now.</div>
              ) : (
                <>
                  {/* Coupon row */}
                  <div className="mb-8 pb-8 border-b border-slate-100">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-3">Have a coupon code?</p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Enter coupon code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        onKeyDown={(e) => e.key === 'Enter' && handleValidateCoupon()}
                        className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-medium placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-300 transition-all"
                      />
                      {couponValidation?.valid ? (
                        <button onClick={handleClearCoupon} className="px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-200 transition-colors">
                          Clear
                        </button>
                      ) : (
                        <button
                          onClick={handleValidateCoupon}
                          disabled={!couponCode.trim() || validatingCoupon}
                          className="px-6 py-2.5 bg-brand-500 text-slate-900 rounded-xl text-sm font-bold hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {validatingCoupon ? '…' : 'Apply'}
                        </button>
                      )}
                    </div>
                    {couponValidation?.valid && (
                      <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2">
                        <CheckIcon className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span className="text-sm font-bold text-emerald-700">
                          {couponValidation.discount_value}{couponValidation.discount_type === 'percentage' ? '%' : '$'} off applied
                          {couponValidation.is_free && ', Free plan!'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Plans grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
                    {cards.map((plan, i) => {
                      const isHov = hoveredPlan === i;
                      const isScale = plan.featured;
                      const isSelected = selectedIdx === i;
                      const showDiscount = couponValidation?.valid && isSelected;

                      return (
                        <div
                          key={plan.id}
                          className="relative flex flex-col rounded-3xl overflow-hidden cursor-pointer bg-white"
                          style={{
                            border: isSelected
                              ? '2px solid #1B3A8A'
                              : isScale ? '2px solid #22c55e' : isHov ? '2px solid #cbd5e1' : '2px solid #e2e8f0',
                            transform: isScale
                              ? isHov || isSelected ? 'translateY(-10px) scale(1.02)' : 'translateY(-6px) scale(1.01)'
                              : isHov || isSelected ? 'translateY(-4px)' : 'translateY(0)',
                            boxShadow: isSelected
                              ? '0 0 0 4px rgba(27,58,138,0.15), 0 16px 40px -8px rgba(27,58,138,0.22)'
                              : isScale
                              ? isHov
                                ? '0 32px 64px -12px rgba(34,197,94,0.25), 0 8px 24px rgba(0,0,0,0.12)'
                                : '0 20px 48px -8px rgba(34,197,94,0.18), 0 8px 24px rgba(0,0,0,0.1)'
                              : isHov ? '0 12px 32px -8px rgba(0,0,0,0.12)' : '0 2px 8px rgba(0,0,0,0.05)',
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                          }}
                          onMouseEnter={() => setHoveredPlan(i)}
                          onMouseLeave={() => setHoveredPlan(null)}
                          onClick={() => setSelectedIdx(i)}
                        >
                          {isSelected && (
                            <span className="absolute top-4 right-4 z-10 w-6 h-6 flex items-center justify-center rounded-full bg-[#1B3A8A] text-white shadow-md">
                              <CheckIcon className="w-3.5 h-3.5" strokeWidth={3} />
                            </span>
                          )}
                          <div className="p-7 flex flex-col flex-1">

                            {/* name + badge */}
                            <div className="flex items-center justify-between mb-5">
                              <span className={`text-[11px] font-black uppercase tracking-widest ${isScale ? 'text-emerald-600' : 'text-slate-400'}`}>
                                {plan.name}
                              </span>
                              {isScale && (
                                <span className="text-[10px] font-black uppercase tracking-wide bg-emerald-500 text-white px-2.5 py-1 rounded-full">
                                  Best value
                                </span>
                              )}
                            </div>

                            {/* price */}
                            <div className="mb-1.5">
                              {showDiscount && couponValidation?.final_price === 0 ? (
                                <div className="flex items-baseline gap-2">
                                  <span className="text-[4rem] font-black tracking-tighter leading-none text-emerald-600">FREE</span>
                                  <span className="text-sm line-through text-slate-400">{plan.price}</span>
                                </div>
                              ) : showDiscount && couponValidation?.final_price !== undefined ? (
                                <div className="flex items-baseline gap-2">
                                  <span className="text-[4rem] font-black tracking-tighter leading-none text-slate-900">${couponValidation.final_price.toFixed(2)}</span>
                                  <span className="text-sm line-through text-slate-400">{plan.price}</span>
                                </div>
                              ) : (
                                <span className="text-[4rem] font-black tracking-tighter leading-none text-slate-900">{plan.price}</span>
                              )}
                            </div>

                            {/* credits + savings */}
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="text-[13.5px] font-semibold text-slate-500">{plan.creditsLabel}</span>
                              {plan.savingsLabel && !showDiscount && (
                                <span className={`text-[11px] font-black rounded-full px-2 py-0.5 ${
                                  isScale ? 'text-green-800 bg-green-500/15' : 'text-brand-700 bg-brand-500/10'
                                }`}>
                                  {plan.savingsLabel}
                                </span>
                              )}
                              {showDiscount && couponValidation?.discount_value && (
                                <span className="text-[11px] font-black rounded-full px-2 py-0.5 text-emerald-700 bg-emerald-500/10">
                                  -{couponValidation.discount_value}{couponValidation.discount_type === 'percentage' ? '%' : '$'} off
                                </span>
                              )}
                            </div>

                            {/* per credit */}
                            <p className="text-[12.5px] font-medium mb-6 text-slate-400">
                              <span className={`font-black ${isScale ? 'text-emerald-600' : 'text-slate-800'}`}>{plan.perCredit}</span> per credit
                            </p>

                            {/* divider */}
                            <div className="h-px mb-5 bg-slate-100" />

                            {/* features */}
                            <ul className="flex flex-col gap-2.5 mb-5">
                              {plan.features.map((f, j) => (
                                <li key={j} className="flex items-start gap-3 text-[13.5px] font-medium">
                                  <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                  <span className="text-slate-700">{f}</span>
                                </li>
                              ))}
                            </ul>

                            {/* spacer */}
                            <div className="flex-1" />

                            {/* CTA */}
                            <button
                              onClick={(e) => { e.stopPropagation(); handlePurchase(i); }}
                              disabled={purchasingId !== null}
                              className={`w-full text-center font-black text-[14.5px] py-4 rounded-2xl transition-all duration-200 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed ${
                                isScale || couponValidation?.is_free
                                  ? 'bg-emerald-500 text-white hover:bg-emerald-400 shadow-lg shadow-emerald-500/20'
                                  : 'bg-slate-100 border-2 border-slate-200 text-slate-900 hover:bg-slate-900 hover:text-white hover:border-slate-900'
                              }`}
                            >
                              {purchasingId === plan.id ? (
                                <span className="flex items-center justify-center gap-2">
                                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                  </svg>
                                  Processing…
                                </span>
                              ) : couponValidation?.is_free ? 'Get Free Plan' : 'Get started'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Custom plans assigned to this user */}
                  {customPlans.length > 0 && (
                    <div className="mt-8 pt-8 border-t border-slate-100">
                      <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-4">Plans available for you</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {customPlans.map((plan) => (
                          <div
                            key={plan.id}
                            className="relative flex flex-col rounded-3xl overflow-hidden bg-white"
                            style={{ border: '2px solid #22c55e', boxShadow: '0 4px 16px rgba(34,197,94,0.12)' }}
                          >
                            <div className="p-7 flex flex-col flex-1">
                              <div className="flex items-center justify-between mb-5">
                                <span className="text-[11px] font-black uppercase tracking-widest text-emerald-600">{plan.name}</span>
                                <span className="text-[10px] font-black uppercase tracking-wide bg-emerald-500 text-white px-2.5 py-1 rounded-full">For you</span>
                              </div>
                              <div className="mb-1.5">
                                <span className="text-[4rem] font-black tracking-tighter leading-none text-slate-900">
                                  ${Number(plan.price_monthly).toFixed(0)}
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5 mb-6">
                                <BoltIcon className="w-4 h-4 text-brand-600" />
                                <span className="text-[13.5px] font-semibold text-slate-500">{Number(plan.credits).toLocaleString()} credits</span>
                              </div>
                              <div className="flex-1" />
                              <button
                                onClick={() => handleCustomPurchase(plan)}
                                disabled={purchasingId !== null}
                                className="w-full text-center font-black text-[14.5px] py-4 rounded-2xl bg-emerald-500 text-white hover:bg-emerald-400 shadow-lg shadow-emerald-500/20 transition-all duration-200 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {purchasingId === plan.id ? (
                                  <span className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    Processing…
                                  </span>
                                ) : 'Buy Now'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-[12px] text-slate-400 mt-8 text-center leading-relaxed">
                    A standard search costs 1 credit. Deep-search and enrichments are priced separately. Credits expire 60 days from purchase.
                  </p>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-8 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-center gap-2">
              <BoltIcon className="w-3.5 h-3.5 text-brand-500" />
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.1em]">Secure Checkout</p>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
